export interface Fix {
  latitude: number;
  longitude: number;
  accuracy: number;
}

const toFix = (p: GeolocationPosition): Fix => ({ latitude: p.coords.latitude, longitude: p.coords.longitude, accuracy: p.coords.accuracy });

let lastKnown: Fix | null = null;

function getPosition(options: PositionOptions): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) {
      reject(Object.assign(new Error('Geolocation unsupported'), { code: 2 }));
      return;
    }
    navigator.geolocation.getCurrentPosition(resolve, reject, options);
  });
}

/**
 * One fresh high-accuracy fix for a scan. Falls back to a coarse or recent fix when
 * the GPS is slow, like the Flutter client falls back to the last known position.
 */
export async function currentPosition(): Promise<Fix> {
  try {
    const fix = toFix(await getPosition({ enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }));
    lastKnown = fix;
    return fix;
  } catch (e) {
    if ((e as GeolocationPositionError).code === 3) {
      try {
        const fix = toFix(await getPosition({ enableHighAccuracy: false, timeout: 8000, maximumAge: 60_000 }));
        lastKnown = fix;
        return fix;
      } catch {
        if (lastKnown) return lastKnown;
      }
    }
    throw e;
  }
}

/** Resolve the permission state to a coarse string without prompting; `null` if unknown. */
export async function queryPermission(): Promise<PermissionState | null> {
  try {
    if (!navigator.permissions?.query) return null;
    return (await navigator.permissions.query({ name: 'geolocation' })).state;
  } catch {
    return null;
  }
}

/** Prompt (if the browser still will) by asking for a position. */
export async function requestPosition(): Promise<'granted' | 'denied' | 'unavailable'> {
  try {
    lastKnown = toFix(await getPosition({ enableHighAccuracy: true, timeout: 15_000, maximumAge: 60_000 }));
    return 'granted';
  } catch (e) {
    const code = (e as GeolocationPositionError).code;
    if (code === 1) return 'denied';
    if (code === 3) return 'granted'; // permission given, no fix yet
    return 'unavailable';
  }
}

export function onPermissionChange(cb: () => void): () => void {
  let status: PermissionStatus | undefined;
  let disposed = false;
  navigator.permissions
    ?.query({ name: 'geolocation' })
    .then((s) => {
      if (disposed) return;
      status = s;
      s.addEventListener('change', cb);
    })
    .catch(() => undefined);
  return () => {
    disposed = true;
    status?.removeEventListener('change', cb);
  };
}
